import { useEffect, useState } from "react";
import {
    Badge, Box, Button, Chip, Collapse, Grid, Paper,
    Stack, TextField, Tooltip, Typography, useTheme
} from "@mui/material";
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import Templete from "../../template/Template";
import { Plus } from "lucide-react";
import Cards from "../../components/Cards/Cards";
import TableData from "../../components/Tables/TableData";
import { formatDate } from "../../utilities/date";
import { displayMoney } from "../../utilities/displayValues";
import { GridColDef } from "@mui/x-data-grid";
import ModalContaPagar from "./ModalContaPagar";
import { isPermite } from "../../utilities/isPermite";
import { ContasPagarType } from "../../types/ContasPagarType";
import { api } from "../../services/api";
import { AlertInfo } from "../../components/Alerts/Alert";
import SimpleBackdrop from "../../components/Loading/SimpleBackdrop";
import moment from "moment";

const MES_INICIO = moment().startOf('month').format('YYYY-MM-DD');
const MES_FIM = moment().endOf('month').format('YYYY-MM-DD');

const STATUS_OPTIONS = [
    { value: '', label: 'Todos' },
    { value: '0', label: 'A Vencer' },
    { value: '1', label: 'Pago' },
    { value: '2', label: 'Em Atraso' },
    { value: '3', label: 'Cancelado' },
];

export default function ContasPagar() {
    const [openLoad, setOpenLoad] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [rows, setRows] = useState<ContasPagarType[]>([]);
    const [contaPagar, setContaPagar] = useState<ContasPagarType | null>(null);
    const [showFiltros, setShowFiltros] = useState(false);
    const [categorias, setCategorias] = useState<{ id_categoria_pagar: number; nome_categoria: string }[]>([]);
    const theme = useTheme();

    const [filtroDescricao, setFiltroDescricao] = useState('');
    const [filtroCategoria, setFiltroCategoria] = useState('');
    const [filtroSituacao, setFiltroSituacao] = useState('');
    const [filtroDataInicio, setFiltroDataInicio] = useState(MES_INICIO);
    const [filtroDataFim, setFiltroDataFim] = useState(MES_FIM);

    const filtrosAtivos = [
        filtroDescricao.trim() !== '',
        filtroCategoria.trim() !== '',
        filtroSituacao !== '',
        filtroDataInicio !== MES_INICIO,
        filtroDataFim !== MES_FIM,
    ].filter(Boolean).length;

    const columns: GridColDef[] = [
        { field: 'descricao', headerName: 'DESCRICAO', width: 300 },
        { field: 'categoria', headerName: 'CATEGORIA', width: 150 },
        {
            field: 'data_vencimento', headerName: 'VENCIMENTO', width: 130,
            valueFormatter: (dt: any) => formatDate(dt),
        },
        {
            field: 'data_pagamento', headerName: 'PAGAMENTO', width: 130,
            valueFormatter: (dt: any) => formatDate(dt),
        },
        {
            field: 'valor_origem', headerName: 'VALOR', width: 130, align: 'right',
            valueFormatter: (dt: any) => displayMoney(dt),
        },
        {
            field: 'situacao_pagar', headerName: 'SITUACAO', width: 130,
            renderCell: (params) => {
                const cod = params.value as number;
                if (cod === 1) return <Chip label="PAGO" color="success" size="small" />;
                if (cod === 2) return <Chip label="EM ATRASO" color="error" size="small" />;
                if (cod === 3) return <Chip label="CANCELADO" size="small" />;
                return <Chip label="A VENCER" color="primary" size="small" />;
            },
        },
    ];

    function handleClick(row: ContasPagarType) {
        if (isPermite('63')) {
            setContaPagar(row);
            setOpenModal(true);
        }
    }

    async function loadData(overrides?: {
        descricao?: string; categoria?: string; situacao?: string;
        inicio?: string; fim?: string;
    }) {
        setOpenLoad(true);
        try {
            const descricao = overrides?.descricao ?? filtroDescricao;
            const categoria = overrides?.categoria ?? filtroCategoria;
            const situacao = overrides?.situacao ?? filtroSituacao;
            const inicio = overrides?.inicio ?? filtroDataInicio;
            const fim = overrides?.fim ?? filtroDataFim;

            const body: any = { data_inicio: inicio, data_fim: fim };
            if (descricao.trim()) body.descricao = descricao.trim();
            if (categoria.trim()) body.categoria = categoria.trim();
            if (situacao !== '') body.situacao_pagar = situacao;

            const response = await api.post('/listcontaspagar', body);
            if (response.data.error) {
                AlertInfo({ icon: 'error', title: 'Falha ao carregar dados' });
            } else {
                const data: ContasPagarType[] = Array.isArray(response.data) ? response.data : [];
                const updated = data.map((item, index) => {
                    let situacao_pagar = item.situacao_pagar;
                    if (situacao_pagar === 0 && moment(item.data_vencimento).isBefore(moment(), 'day')) {
                        situacao_pagar = 2;
                    }
                    return { ...item, situacao_pagar, id: index };
                });
                setRows(updated);
            }
        } catch {
            AlertInfo({ icon: 'error', title: 'Falha na conexao' });
        }
        setOpenLoad(false);
    }

    function handleLimpar() {
        setFiltroDescricao('');
        setFiltroCategoria('');
        setFiltroSituacao('');
        setFiltroDataInicio(MES_INICIO);
        setFiltroDataFim(MES_FIM);
        loadData({ descricao: '', categoria: '', situacao: '', inicio: MES_INICIO, fim: MES_FIM });
    }

    const aVencer = rows.filter(r => r.situacao_pagar === 0);
    const emAtraso = rows.filter(r => r.situacao_pagar === 2);
    const pagos = rows.filter(r => r.situacao_pagar === 1);
    const totalAVencer = aVencer.reduce((acc, r) => acc + Number(r.valor_origem || 0), 0);
    const totalEmAtraso = emAtraso.reduce((acc, r) => acc + Number(r.valor_origem || 0), 0);
    const totalPagos = pagos.reduce((acc, r) => acc + Number(r.valor_pago || r.valor_origem || 0), 0);

    useEffect(() => {
        loadData();
        api.get('/categoriaspagar')
            .then(res => { if (Array.isArray(res.data)) setCategorias(res.data); })
            .catch(() => { });
    }, []);

    return (
        <Templete menuActive="contas_pagar">
            <SimpleBackdrop open={openLoad} />
            <ModalContaPagar
                open={openModal}
                setOpen={setOpenModal}
                contaPagar={contaPagar}
                setContaPagar={setContaPagar}
                reload={loadData}
            />

            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={4}>
                    <Cards color={theme.palette.primary.main} label="A VENCER"
                        main={String(aVencer.length)} value={displayMoney(totalAVencer)} />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Cards color={theme.palette.error.main} label="EM ATRASO"
                        main={String(emAtraso.length)} value={displayMoney(totalEmAtraso)} />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Cards color={theme.palette.success.main} label="PAGAS"
                        main={String(pagos.length)} value={displayMoney(totalPagos)} />
                </Grid>
            </Grid>

            {/* Barra de acoes */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="h6" fontWeight={600}>Contas a Pagar</Typography>
                <Stack direction="row" spacing={1}>
                    <Tooltip title={filtrosAtivos > 0 ? `Limpar (${filtrosAtivos})` : 'Limpar filtros'}>
                        <span>
                            <Button
                                size="small"
                                color="inherit"
                                startIcon={<CloseIcon />}
                                onClick={handleLimpar}
                                disabled={filtrosAtivos === 0}
                            >
                                Limpar {filtrosAtivos > 0 ? `(${filtrosAtivos})` : ''}
                            </Button>
                        </span>
                    </Tooltip>
                    <Badge badgeContent={filtrosAtivos} color="primary">
                        <Button
                            size="small"
                            variant={showFiltros ? 'contained' : 'outlined'}
                            startIcon={<FilterListIcon />}
                            onClick={() => setShowFiltros(v => !v)}
                        >
                            Filtros
                        </Button>
                    </Badge>
                    {isPermite('62') && (
                        <Button
                            size="small"
                            variant="contained"
                            startIcon={<Plus size={16} />}
                            onClick={() => { setContaPagar(null); setOpenModal(true); }}
                        >
                            Nova Conta
                        </Button>
                    )}
                </Stack>
            </Stack>

            {/* Filtros colapsaveis */}
            <Grid container>
                <Grid item xs={12}>
                    <Collapse in={showFiltros} sx={{bgcolor: '#fff', mb: 2}}>
                        {/* <Paper variant="outlined" sx={{ p: 1, mb: 2, bgcolor: '#fafafa' }}> */}
                            <Grid container spacing={1}>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        label="Descricao"
                                        size="small"
                                        fullWidth
                                        value={filtroDescricao}
                                        onChange={e => setFiltroDescricao(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && loadData()}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        label="Categoria"
                                        select
                                        size="small"
                                        fullWidth
                                        value={filtroCategoria}
                                        onChange={e => setFiltroCategoria(e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        SelectProps={{ native: true }}
                                    >
                                        <option value="">Todas</option>
                                        {categorias.map(c => (
                                            <option key={c.id_categoria_pagar} value={c.nome_categoria}>
                                                {c.nome_categoria}
                                            </option>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        label="Situacao"
                                        select
                                        size="small"
                                        fullWidth
                                        value={filtroSituacao}
                                        onChange={e => setFiltroSituacao(e.target.value)}
                                        SelectProps={{ native: true }}
                                        InputLabelProps={{ shrink: true }}
                                    >
                                        {STATUS_OPTIONS.map(o => (
                                            <option key={o.value} value={o.value}>{o.label}</option>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <TextField
                                        label="Vencimento De"
                                        type="date"
                                        size="small"
                                        fullWidth
                                        value={filtroDataInicio}
                                        onChange={e => setFiltroDataInicio(e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <TextField
                                        label="Vencimento Ate"
                                        type="date"
                                        size="small"
                                        fullWidth
                                        value={filtroDataFim}
                                        onChange={e => setFiltroDataFim(e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <Button variant="contained" fullWidth startIcon={<SearchIcon />} onClick={() => loadData()}>
                                        BUSCAR
                                    </Button>
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <Button variant="outlined" fullWidth startIcon={<CloseIcon />} onClick={handleLimpar}>
                                        LIMPAR
                                    </Button>
                                </Grid>
                            </Grid>
                        {/* </Paper> */}
                    </Collapse>
                </Grid>
            </Grid>

            <TableData
                rows={rows}
                columns={columns}
                handleClick={(row) => handleClick(row as ContasPagarType)}
            />
        </Templete>
    );
}
